import { Tooltip as Primitive } from "radix-ui";
import type { FocusEvent, ReactNode } from "react";
import { pixels } from "../../theme/pixels.ts";
import { useOverlayStage, useStageBoundary } from "../portalContainer.tsx";

/**
 * FR-015-101, § Overlays, Tooltip, taken from shadcn/ui's Tooltip over Radix (Spectrum UI has
 * none) and themed through the variables of `packages/tokens` (2026-09-15, by the owner's
 * decision): shown on hover and on keyboard focus, dismissible with `Esc` (SC 1.4.13), carrying no
 * action. Baton's one-call API stays over shadcn/ui's four parts, with a provider per tooltip
 * so a tooltip outside the shell still renders with its own delay.
 *
 * This module is the tip itself, the trigger, the portal, the content and the arrow, with the words
 * the host hands it; `Tooltip.tsx` draws a trigger's shortcut after the words (§ Key hints) over it.
 * The two are apart (2026-10-22) because a disabled button's reason is a tip that never carries a
 * shortcut and is loaded as a chunk of its own (`primitives/buttons/unavailable.tsx`): read through
 * `Tooltip`, that chunk depended on the key hint, which `Button` draws in the first load of both
 * entries, and rolldown's fold of what a lazily loaded chunk finds already loaded then kept `Button`
 * and `ErrorState` out of the shared chunk as two files of their own, 2.0 KB gzipped more on the
 * public entry for the same code (FR-015-120, `scripts/size.ts`). The reason's chunk reads this
 * module, which depends on Radix and the pixel reader alone.
 *
 * What differs from the source, and why: the enter and exit run on `--duration-base` and the
 * entrance easing, so `data-motion="reduced"` resolves them to zero (§ Motion); the width is
 * capped at the small dialog's measure as the Baton tooltip was; the arrow's 2 px lift is
 * the `-mt-0.5` step and its corner the `rounded-xs` radius, where the source writes both as
 * arbitrary lengths; the layer is `--layer-tooltip`, one step over every other overlay, where the
 * source writes `z-50`; the gap to the trigger and the padding from the viewport's edge are the
 * `--space-2` and `--space-3` steps, where the source writes 4 and 8 px; and `followsTrigger`
 * measures the trigger on every frame while the tip is shown, Radix's `always` position strategy,
 * for a trigger that moves under it, as the avatar stack's lifted and fanning avatar does.
 *
 * Where it renders (2026-10-01, by the owner's report: "the pop up tips get hidden under the bar..."):
 * always in the document body, never in the floating panels' container (`portalContainer.tsx`). A tip
 * is not interactive, so the reason a panel inside a modal dialog portals into the dialog, the dialog's
 * scroll and pointer lock, does not apply to it, and in the body no container that clips its overflow
 * and no stacking context below the shell can cut it. Radix keeps it inside the viewport, its default
 * collision boundary, with `--space-3` of padding from the viewport's edge. The one exception is the
 * component gallery's panel, the overlay stage of `portalContainer.tsx`, which no screen provides.
 *
 * When focus opens it (2026-10-02, by the owner's report: "please dont show this tooltip when we first
 * open it... a lot of pages do that"): only keyboard focus, the trigger matching `:focus-visible`, the
 * browser's own judgement of whether the person is on the keyboard. Radix opens a tip on any focus but
 * one from a press on the trigger itself, so a popover, menu or sheet opened with a click that moves the
 * focus onto an icon button inside it opened that button's tip before anything was done. The trigger's
 * `onFocus` prevents the event's default when the focus is not visible, which Radix's composed handler
 * reads as the host taking the event and so does not open the tip. Hover opens it as before, and a
 * person tabbing onto the trigger still gets it (SC 1.4.13, § Iconography).
 */
export interface TooltipOverlayProps {
  readonly content: ReactNode;
  readonly delay?: number;
  readonly placement?: "top" | "bottom" | "left" | "right";
  /** True when the trigger moves while the tip is shown, so the tip is placed again on every frame and moves with it. */
  readonly followsTrigger?: boolean;
  /** Controlled visibility, for a host that shows the tooltip only some of the time (a collapsible button, only while its label is collapsed). */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly children: ReactNode;
}

/** Keeps the tip shut for focus the person did not move with the keyboard, such as an overlay placing it on open. */
function openOnKeyboardFocusOnly(event: FocusEvent<HTMLElement>): void {
  if (!event.currentTarget.matches(":focus-visible")) event.preventDefault();
}

/** The tip's gap from its trigger. */
const OFFSET = pixels("space-2");
/** The least room the tip keeps from the viewport's edge when Radix shifts or flips it. */
const COLLISION_PADDING = pixels("space-3");

export function TooltipOverlay({ content, delay = 400, placement = "top", followsTrigger = false, open, onOpenChange, children }: TooltipOverlayProps) {
  const overlayStage = useOverlayStage();
  const boundary = useStageBoundary();
  return (
    <Primitive.Provider delayDuration={delay}>
      <Primitive.Root {...(open === undefined ? {} : { open })} {...(onOpenChange === undefined ? {} : { onOpenChange })}>
        <Primitive.Trigger asChild onFocus={openOnKeyboardFocusOnly}>
          {children}
        </Primitive.Trigger>
        <Primitive.Portal {...(overlayStage === undefined ? {} : { container: overlayStage })}>
          <Primitive.Content
            data-slot="tooltip-content"
            {...boundary}
            side={placement}
            sideOffset={OFFSET}
            updatePositionStrategy={followsTrigger ? "always" : "optimized"}
            collisionPadding={COLLISION_PADDING}
            className="z-(--layer-tooltip) w-fit max-w-(--dialog-width-sm) origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-foreground px-3 py-1.5 text-xs text-balance text-background fade-in-0 zoom-in-95 duration-(--duration-base) ease-out data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
          >
            {content}
            <Primitive.Arrow className="z-(--layer-tooltip) -mt-0.5 size-2.5 -translate-y-1/2 rotate-45 rounded-xs bg-foreground fill-foreground" />
          </Primitive.Content>
        </Primitive.Portal>
      </Primitive.Root>
    </Primitive.Provider>
  );
}
